import {RecursoLogsPage} from '@components/recurso/pages/RecursoLogsPage.tsx';
import {RecursoParametrosPage} from '@components/recurso/pages/RecursoParametrosPage.tsx';
import {DefaultContainer} from "@shared/DefaultContainer.tsx";
import {createSearchParams, useLocation, useNavigate, useSearchParams} from "react-router";
import {IRecurso} from "@models/recurso/IRecurso.ts";
import React, {useEffect, useState} from "react";
import {_RecursosEndpoints} from "@localization/siteMap/endpoints/_RecursosEndpoints.ts";
import {RecursoInformacao, RecursoInformacaoGrid} from "@components/recurso/RecursoInformacao.tsx";
import {Grid, IconButton, Paper, Tab, Tabs} from "@mui/material";
import {RecursoMembrosPage} from "@components/recurso/pages/RecursoMembrosPage.tsx";
import {RecursosPage} from "@components/recurso/pages/RecursosPage.tsx";
import {SolicitacoesPage} from "@components/solicitacoes/pages/solicitacoes/SolicitacoesPage.tsx";
import {RecursoMovimentacoesPage} from "@components/recurso/pages/movimentacoes/RecursoMovimentacoesPage.tsx";
import {RecursoSolicitacaoTransferenciaPage} from "@components/recurso/pages/RecursoSolicitacaoTransferenciaPage.tsx";
import {ContratosPage} from "@components/contratos/pages/ContratosPage.tsx";
import {RecursoContaContabilLimitePage} from "@components/recurso/pages/RecursoContaContabilLimitePage.tsx";
import {OpenInNew} from "@mui/icons-material";
import {useApi} from "@contexts/hooks/api/useApi.ts";
import {useAuth} from "@contexts/hooks/UseAuth.tsx";

type tabs = 'InformacoesBasicas' | 'Parametros' | 'RecursoMembros' | 'RecursoFilhos' | 'SolicitacoesCompra' |
	'SolicitacoesRecurso' | 'Movimentacoes' | 'Contratos' | 'VinculacaoContaContabil' | 'Logs'


/**
 * @path '/recurso/detalhes'
 */
export const RecursoPage: React.FC = () => {
		const {hasAccess} = useAuth();
		const {Get} = useApi()
		const [recurso, setRecurso] = useState<IRecurso | null>(null);
		const [refreshRequested, setRefreshRequested] = useState(false);
		const [searchParams] = useSearchParams();
		const navigate = useNavigate();
		const location = useLocation();
		const [tab, setTab] = useState<tabs>('InformacoesBasicas');

		const podeAlterar = hasAccess(['EditarRecurso']);

		const recursoId = searchParams.get('recursoId');
		useEffect(() => {
			setTab((location.hash.slice(1)) as tabs || 'InformacoesBasicas');
		}, [location.hash]);

		useEffect(() => {
			if (recursoId) {
				Get(_RecursosEndpoints.GetRecurso({
					ids: [Number(recursoId)],
					deveIncluir: ["UsuarioCriacao", "RecursoBase"],
					layers: ['Valores'],
				}, {
					page: 1,
					pageSize: 1,
				}), refreshRequested).then(x => {
					if (x.status == 'Success') {
						setRecurso(x.items[0]);
					}
				}).finally(() => {
					setRefreshRequested(false);
				});
			}
		}, [recursoId, refreshRequested]);


		return (<DefaultContainer permissaoNecessaria={['VisualizarRecursoInformacoes']}
		                          component={Paper}
		                          titulo={'Detalhes do recurso'}
		                          size={'large'}>
				{recurso && <Grid container spacing={1} alignContent={'center'} height={'100%'}>
					<Grid size={12}>
						<RecursoInformacao value={recurso} variant={'list'} onUpdate={() => setRefreshRequested(true)}/>
					</Grid>

					<Tabs value={tab}
					      variant={'scrollable'}
					      style={{textDecoration: 'none'}}
					      onChange={(_, v) => {
						      navigate({
							      pathname: location.pathname,
							      search: location.search,
							      hash: `#${v}`,
						      });
					      }}>
						<Tab value={'InformacoesBasicas'} label="Informações basicas"/>
						<Tab value={'Parametros'} label="Parametros"/>
						<Tab value={'RecursoMembros'} label="Membros"/>
						<Tab value={'RecursoFilhos'} label="Recursos relacionados"/>
						<Tab value={'Contratos'} label="Contratos"/>
						<Tab value={'VinculacaoContaContabil'} label="Vinculação conta contábil"/>
						<Tab value={'SolicitacoesCompra'} label={'Solicitações compra'}/>
						<Tab value={'SolicitacoesRecurso'} label={'Solicitações recurso'}/>
						<Tab value={'Movimentacoes'} label="Movimentações"/>
						<Tab value={'Logs'} label="Logs"/>
					</Tabs>
					<Grid size={12}>
						<Grid component={Paper} elevation={2} p={1}>
							{tab === 'InformacoesBasicas' &&
								<RecursoInformacaoGrid readonly={podeAlterar == false} value={recurso}
								                       variant={'grid'}
								                       onUpdate={x => setRecurso(x)}/>
							}
							{tab === 'Parametros' &&
								<RecursoParametrosPage preset={{recurso}} onUpdate={() => setRefreshRequested(true)}/>}
							{tab === 'RecursoMembros' &&
								<RecursoMembrosPage filtros={{idsRecurso: [recurso.id]}} recurso={recurso}/>}
							{tab === 'RecursoFilhos' &&
								<RecursosPage disableHeader disableTitle filtros={{recursoBase: [recurso.id]}}/>}
							{tab === 'VinculacaoContaContabil' &&
								<RecursoContaContabilLimitePage hideHeader recursoId={recurso.id}/>}
							{tab === 'Contratos' && <ContratosPage recursoIds={[recurso.id]}/>}
							{tab === 'SolicitacoesCompra' && <SolicitacoesPage filtros={{recursoId: [recurso.id]}}
							                                                   recurso={recurso}
							                                                   hideHeader
							                                                   slotProps={{
								                                                   solicitacaoInformacao: {
									                                                   EndComponent: (v) =>
										                                                   <IconButton onClick={() => {
											                                                   navigate({
												                                                   pathname: '/solicitacao/detalhes',
												                                                   search: createSearchParams({
													                                                   solicitacaoId: v.id.toString(),
												                                                   }).toString(),
											                                                   });
										                                                   }}>
											                                                   <OpenInNew/>
										                                                   </IconButton>,
								                                                   },
							                                                   }}

							/>}
							{tab === 'SolicitacoesRecurso' &&
								<RecursoSolicitacaoTransferenciaPage filtros={{recursoIds: [recurso.id]}}/>}
							{tab === 'Movimentacoes' &&
								<RecursoMovimentacoesPage onCreate={() => setRefreshRequested(true)}
								                          recursoId={recurso.id}/>}
							{tab === 'Logs' && <RecursoLogsPage/>}
						</Grid>
					</Grid>
				</Grid>
				}
			</DefaultContainer>
		);
	}
;